/**
 * The merge-field chip: an atomic, non-editable inline node inserted from
 * the "Insert field" dropdown (see RichTextEditor.tsx) instead of staff
 * hand-typing `{{token}}` text.
 *
 * `renderHTML`'s text content is the literal `{{token}}` string — that is
 * what keeps the backend's existing `applyMergeFields`/`applyMergeFieldsHtml`
 * regex substitution (apps/api/src/lib/document-merge-fields.ts) working
 * completely unchanged: HTML tag syntax never matches its `\{\{...\}\}`
 * regex, so the token text sitting inside this span's one text node gets
 * found and replaced exactly like it does in a hand-typed plain-text body.
 * The on-screen appearance (a labeled pill, MergeFieldChip.tsx) and this
 * storage serialization deliberately diverge — that's what Tiptap's
 * `NodeView` vs `renderHTML` split is for.
 *
 * Composing a template always starts blank via the "Insert field" dropdown
 * (insert-only versioning — there is no "load a past version back into the
 * editor" flow), but `parseHTML` below is a real code path, not dead
 * scaffolding: `sample-body-templates.ts`'s per-kind starter content is
 * authored as this exact span markup so loading it seeds the editor with
 * real chips, not raw `{{token}}` text — `data-merge-field-label` exists
 * specifically so that round-trip recovers the human label too, not just
 * the token. `html-sanitize.ts`'s allowlist permits both attributes.
 */

import { Node, ReactNodeViewRenderer, mergeAttributes } from '@tiptap/react';
import { MergeFieldChip } from './MergeFieldChip.js';

export interface MergeFieldAttrs {
  token: string;
  label: string;
}

export const MergeFieldNode = Node.create({
  name: 'mergeField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      token: { default: '' },
      label: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-merge-field]',
        getAttrs: (element: HTMLElement) => ({
          token: element.getAttribute('data-merge-field') ?? '',
          label: element.getAttribute('data-merge-field-label') ?? '',
        }),
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-merge-field': node.attrs.token as string,
        'data-merge-field-label': node.attrs.label as string,
        class: 'merge-field-chip',
      }),
      `{{${node.attrs.token as string}}}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MergeFieldChip);
  },
});
